<script lang="ts" setup>
import type { IProps } from './Table.types'
const props = withDefaults(defineProps<IProps>(), {
  options: () => ({
    highlightFirstColumn: false,
    align: 'right',
  }),
  summary: undefined,
})

const contentClasses = computed(() => {
  return [
    'table__content',
    props.options?.highlightFirstColumn ? 'table__content--highlight-first-column' : '',
    props.options?.align === 'left' ? 'table__content--align-left' : '',
  ]
})

const escapeHtml = (value: string) => {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

const tableHtml = computed(() => {
  const html = props.table.table

  if (!html || !props.summary?.length) {
    return html
  }

  const cells = props.summary
    .map(value => `<td><p>${escapeHtml(value)}</p></td>`)
    .join('')

  return html.replace('</table>', () => `<tr class="table__summary">${cells}</tr></table>`)
})
</script>

<template>
  <article class="table">
    <div
      v-if="table.table"
      :class="contentClasses"
      v-html="tableHtml"
    />

    <slot />
  </article>
  
    <div
    v-if="table.info"
    class="table__info"
      v-html="table.info"
    />
</template>

<style lang="scss" scoped>
.table {
  width: 100%;

  &__title {
    @include h8;
    margin-bottom: 1.2rem;
  }

  &__content {
    @include body-3;
    overflow-x: scroll;

    :deep(tbody) {
      border:none;
    }

    :deep(table) {
      width: 100%;
      border: 1px solid $neutral-300;
      border-radius: 1rem;
      border-collapse: separate;
      border-spacing: 0;
    }

    :deep(th) {
      @include body-3-medium;
      padding: 1.2rem 2.4rem;
      text-align: right;
      vertical-align: middle;
      background-color: $gray-200;
      border-right: 1px solid $neutral-300;
      border-bottom: 1px solid $neutral-300;
    }

    :deep(th:first-child) {
      border-top-left-radius: 1rem;
    }

    :deep(th:last-child) {
      border-right: none;
      border-top-right-radius: 1rem;
    }

    :deep(td) {
      @include body-3;
      padding: 1.2rem 2.4rem;
      white-space: nowrap;
      text-align: right;
      vertical-align: middle;
      border-right: 1px solid $neutral-300;
      border-bottom: 1px solid $neutral-300;
    }

    :deep(tr:last-child td) {
      border-bottom: none;
    }

    :deep(tr:last-child td:first-child) {
      border-bottom-left-radius: 1rem;
    }

    :deep(tr td:last-child) {
      border-right: none;
    }

    :deep(tr.table__summary td) {
      @include body-2;
      color: $text-400;
      font-weight: 700;
      background-color: $gray-100;
      border-top: 1px solid $neutral-300;
    }

    &.table__content--align-left :deep(th),
    &.table__content--align-left :deep(td) {
      text-align: left;
    }

    &.table__content--highlight-first-column :deep(th:first-child) {
      text-align: left;
    }
    
    &.table__content--highlight-first-column :deep(td:first-child) {
      text-align: left;
      background-color: $gray-100;
    }
  }

  &__info {
    @include body-3;
    margin-top: 0.8rem;
    color: $text-300;

    :deep(p) {
      margin: 0 0 0.8rem;
    }

    :deep(p:last-child) {
      margin-bottom: 0;
    }
  }
}

@include atM {
  .table {
    &__content {
      overflow-x: auto;
    }
  }
}
</style>

